// Mock data for rules, preferences, and automation profiles - SAT V4 Zone
import type {
  AutomationProfile,
  HardRule,
  Preference,
  ProblemSolution,
  ReviewListItem,
  SoftRule,
  UnifiedRule,
} from '../../types';

export const mockHardRules: HardRule[] = [
  {
    id: 'hr-001',
    name: 'No Teacher Double Booking',
    description: 'A teacher cannot be scheduled for two lessons at the same time',
    type: 'teacher_availability',
    category: 'teacher',
    condition: 'teacher.lessons.filter(l => l.period === currentPeriod).length <= 1',
    isActive: true,
    isSystemRule: true,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'hr-002',
    name: 'No Room Double Booking',
    description: 'A room cannot be assigned to two classes at the same time',
    type: 'room_availability',
    category: 'room',
    condition: 'room.lessons.filter(l => l.period === currentPeriod).length <= 1',
    isActive: true,
    isSystemRule: true,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'hr-003',
    name: 'Room Capacity Must Not Be Exceeded',
    description: 'The number of pupils in a class must not exceed the room capacity',
    type: 'room_availability',
    category: 'room',
    condition: 'class.pupils.length <= room.capacity',
    isActive: true,
    isSystemRule: true,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'hr-004',
    name: 'Teacher Must Be Available',
    description: 'Teachers cannot be scheduled during periods they have marked as unavailable',
    type: 'teacher_availability',
    category: 'teacher',
    condition: 'teacher.availability[day][period] !== "unavailable"',
    isActive: true,
    isSystemRule: true,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'hr-005',
    name: 'Class Cannot Have Multiple Lessons Same Period',
    description: 'A class cannot be scheduled for two different lessons at the same time',
    type: 'year_group',
    category: 'class',
    condition: 'class.lessons.filter(l => l.period === currentPeriod).length <= 1',
    isActive: true,
    isSystemRule: true,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'hr-006',
    name: 'PE Requires Sports Facility',
    description: 'Physical Education lessons must be scheduled in gym or sports hall',
    type: 'room_availability',
    category: 'room',
    condition: 'subject.code !== "PE" || room.roomType in ["gym", "hall"]',
    isActive: true,
    isSystemRule: false,
    createdAt: '2024-02-01T09:00:00Z',
  },
  {
    id: 'hr-007',
    name: 'Science Requires Lab',
    description: 'Science lessons (Physics, Chemistry, Biology) must be scheduled in labs',
    type: 'room_availability',
    category: 'room',
    condition: 'subject.department !== "Science" || room.roomType === "lab"',
    isActive: true,
    isSystemRule: false,
    createdAt: '2024-02-01T09:00:00Z',
  },
];

export const mockSoftRules: SoftRule[] = [
  {
    id: 'sr-001',
    name: 'Minimize Consecutive Lessons for Teachers',
    description: 'Teachers should not have more than 4 consecutive lessons without a break',
    type: 'no_consecutive',
    category: 'teacher',
    condition: 'teacher.consecutiveLessons <= 4',
    weight: 80,
    isActive: true,
    createdAt: '2024-01-20T14:00:00Z',
  },
  {
    id: 'sr-002',
    name: 'Prefer Morning for Core Subjects',
    description: 'English, Maths, and Science are preferred in morning slots',
    type: 'subject_timing',
    category: 'curriculum',
    condition: 'subject.isCore && period.order <= 4',
    weight: 60,
    isActive: true,
    createdAt: '2024-01-20T14:00:00Z',
  },
  {
    id: 'sr-003',
    name: 'Avoid Last Period for PE',
    description: 'PE lessons should not be scheduled in the last period of the day',
    type: 'subject_timing',
    category: 'curriculum',
    condition: 'subject.code !== "PE" || period.order < 6',
    weight: 50,
    isActive: true,
    createdAt: '2024-01-20T14:00:00Z',
  },
  {
    id: 'sr-004',
    name: 'Balance Teacher Workload Across Week',
    description: 'Teachers should have similar number of lessons each day',
    type: 'max_per_day',
    category: 'teacher',
    condition: 'Math.abs(teacher.lessonsPerDay[day] - teacher.averageLessonsPerDay) <= 1',
    weight: 70,
    isActive: true,
    createdAt: '2024-01-25T11:00:00Z',
  },
  {
    id: 'sr-005',
    name: 'Respect Teacher Room Preferences',
    description: 'Assign teachers to their preferred rooms when possible',
    type: 'room_availability',
    category: 'teacher',
    condition: 'teacher.preferredRooms.includes(room.id)',
    weight: 40,
    isActive: true,
    createdAt: '2024-01-25T11:00:00Z',
  },
  {
    id: 'sr-006',
    name: 'Minimize Room Changes for Classes',
    description: 'Classes should have lessons in the same building where possible',
    type: 'room_availability',
    category: 'class',
    condition: 'class.buildingChanges <= 2',
    weight: 55,
    isActive: true,
    createdAt: '2024-02-05T16:00:00Z',
  },
];

export const mockPreferences: Preference[] = [
  {
    id: 'pref-001',
    name: 'Year 11 Priority Morning Slots',
    description: 'Year 11 GCSE classes should have priority for morning lesson slots',
    priority: 1,
    type: 'general',
    isActive: true,
    createdAt: '2024-01-30T10:00:00Z',
  },
  {
    id: 'pref-002',
    name: 'Minimize Split Lessons',
    description: 'Double lessons should be consecutive, not split across the day',
    priority: 2,
    type: 'general',
    isActive: true,
    createdAt: '2024-01-30T10:00:00Z',
  },
  {
    id: 'pref-003',
    name: 'Computer Rooms for ICT',
    description: 'ICT lessons should always be in computer rooms',
    priority: 1,
    type: 'room',
    targetId: 'subj-ict',
    isActive: true,
    createdAt: '2024-02-01T09:00:00Z',
  },
];

export const mockTeacherPreferences: Preference[] = [
  {
    id: 'tpref-001',
    name: 'Mr Smith Morning Preference',
    description: 'Mr Smith prefers to teach in the morning',
    priority: 2,
    type: 'teacher',
    targetId: 'teacher-001',
    isActive: true,
    createdAt: '2024-02-10T14:00:00Z',
  },
  {
    id: 'tpref-002',
    name: 'Mrs Johnson Ground Floor Only',
    description: 'Mrs Johnson requires ground floor rooms due to mobility',
    priority: 1,
    type: 'teacher',
    targetId: 'teacher-002',
    isActive: true,
    createdAt: '2024-02-10T14:00:00Z',
  },
];

export const mockProblemSolutions: ProblemSolution[] = [
  {
    id: 'ps-001',
    title: 'Design Technology Room Capacity Issue',
    problemDescription:
      '1. The School Design Technology Classroom has a Maximum of 20 Pupil Capacity per Lesson\n2. The School average Class Size is 30 Pupils\n3. Therefore there are too many Pupils in a Class and cannot accommodate all of the Pupils into a Design Technology Classroom at the same time',
    solutionDescription:
      '1. Combine 2 Classes (of 30 each) = 60 Pupils Total\n2. Create 3 x Design Technology (DT) Classes of 20 Pupils in each DT Class\n3. When 20 Pupils are in a DT Class the remaining 10 Pupils are in or added to a different Class eg) Computer Science (this repeats until all 60 Pupils have attended their DT Class)',
    category: 'resources',
    affectedAreas: ['Design Technology', 'Computer Science', 'Year 10', 'Year 11'],
    createdBy: 'teacher-001',
    createdAt: '2024-02-15T11:30:00Z',
    updatedAt: '2024-02-15T11:30:00Z',
    isApplied: true,
  },
  {
    id: 'ps-002',
    title: 'Science Lab Shortage',
    problemDescription:
      '1. Only 6 science labs available\n2. Need to schedule 8 science classes simultaneously in Period 3\n3. Not enough lab space for all classes',
    solutionDescription:
      '1. Stagger science lessons across different periods\n2. Use theory-based lessons in regular classrooms when practical work not required\n3. Prioritize lab use for practical assessment periods',
    category: 'resources',
    affectedAreas: ['Science Department', 'Period 3', 'Labs'],
    createdBy: 'teacher-005',
    createdAt: '2024-02-20T09:00:00Z',
    updatedAt: '2024-02-20T09:00:00Z',
    isApplied: true,
  },
];

export const mockAutomationProfiles: AutomationProfile[] = [
  {
    id: 'profile-001',
    name: 'Balanced Default',
    description: 'Standard balanced approach to timetable generation',
    settings: {
      prioritizeTeacherPreferences: 5,
      prioritizeRoomEfficiency: 5,
      minimizeConflicts: 8,
      balanceWorkload: 7,
      respectSoftRules: 6,
    },
    isDefault: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'profile-002',
    name: 'Teacher Focused',
    description: 'Prioritizes teacher preferences and workload balance',
    settings: {
      prioritizeTeacherPreferences: 9,
      prioritizeRoomEfficiency: 4,
      minimizeConflicts: 7,
      balanceWorkload: 9,
      respectSoftRules: 7,
    },
    isDefault: false,
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: '2024-02-01T00:00:00Z',
  },
  {
    id: 'profile-003',
    name: 'Room Optimized',
    description: 'Maximizes room utilization and minimizes movement between buildings',
    settings: {
      prioritizeTeacherPreferences: 4,
      prioritizeRoomEfficiency: 10,
      minimizeConflicts: 8,
      balanceWorkload: 5,
      respectSoftRules: 5,
    },
    isDefault: false,
    createdAt: '2024-02-15T00:00:00Z',
    updatedAt: '2024-02-15T00:00:00Z',
  },
];

// Unified Rules for new structure (SAT Rules, Select Rules, Customised Rules)
export const mockUnifiedRules: UnifiedRule[] = [
  // SAT Rules - Core system rules that are always applied
  {
    id: 'sat-001',
    name: 'No Teacher Double Booking',
    category: 'Teacher Availability',
    description: 'A teacher cannot be scheduled for two lessons at the same time',
    ruleType: 'SAT Rule',
  },
  {
    id: 'sat-002',
    name: 'No Room Double Booking',
    category: 'Room Availability',
    description: 'A room cannot be assigned to two classes at the same time',
    ruleType: 'SAT Rule',
  },
  {
    id: 'sat-003',
    name: 'Room Capacity Must Not Be Exceeded',
    category: 'Room Availability',
    description: 'The number of pupils in a class must not exceed the room capacity',
    ruleType: 'SAT Rule',
  },
  {
    id: 'sat-004',
    name: 'Teacher Must Be Available',
    category: 'Teacher Availability',
    description: 'Teachers cannot be scheduled during periods they have marked as unavailable',
    ruleType: 'SAT Rule',
  },
  {
    id: 'sat-005',
    name: 'Class Cannot Have Multiple Lessons Same Period',
    category: 'Year Group',
    description: 'A class cannot be scheduled for two different lessons at the same time',
    ruleType: 'SAT Rule',
  },
  // Select Rules - Optional rules that can be applied
  {
    id: 'sel-001',
    name: 'PE Requires Sports Facility',
    category: 'Room Availability',
    description: 'Physical Education lessons must be scheduled in gym or sports hall',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-002',
    name: 'Science Requires Lab',
    category: 'Room Availability',
    description: 'Science lessons (Physics, Chemistry, Biology) must be scheduled in labs',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-003',
    name: 'Minimize Consecutive Lessons for Teachers',
    category: 'No Consecutive',
    description: 'Teachers should not have more than 4 consecutive lessons without a break',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-004',
    name: 'Prefer Morning for Core Subjects',
    category: 'Subject Timing',
    description: 'English, Maths, and Science are preferred in morning slots',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-005',
    name: 'Avoid Last Period for PE',
    category: 'Subject Timing',
    description: 'PE lessons should not be scheduled in the last period of the day',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-006',
    name: 'Balance Teacher Workload Across Week',
    category: 'Maximum Per Day',
    description: 'Teachers should have similar number of lessons each day',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-007',
    name: 'Year 11 Priority Morning Slots',
    category: 'General',
    description: 'Year 11 GCSE classes should have priority for morning lesson slots',
    ruleType: 'Select Rule',
  },
  {
    id: 'sel-008',
    name: 'Minimize Split Lessons',
    category: 'General',
    description: 'Double lessons should be consecutive, not split across the day',
    ruleType: 'Select Rule',
  },
  // Customised Rules - User-defined rules
  {
    id: 'cust-001',
    name: 'Computer Rooms for ICT',
    category: 'Room Availability',
    description: 'ICT lessons should always be in computer rooms',
    ruleType: 'Customised Rule',
  },
  {
    id: 'cust-002',
    name: 'Mr Smith Morning Preference',
    category: 'Teacher Availability',
    description: 'Mr Smith prefers to teach in the morning',
    ruleType: 'Customised Rule',
  },
  {
    id: 'cust-003',
    name: 'Mrs Johnson Ground Floor Only',
    category: 'Teacher Availability',
    description: 'Mrs Johnson requires ground floor rooms due to mobility',
    ruleType: 'Customised Rule',
  },
];

// Initial Review List - SAT Rules are always in review list by default, Customised Rules pre-applied
export const mockInitialReviewList: ReviewListItem[] = [
  // SAT Rules - always Hard Rule
  { ruleId: 'sat-001', applyAs: 'Hard Rule' },
  { ruleId: 'sat-002', applyAs: 'Hard Rule' },
  { ruleId: 'sat-003', applyAs: 'Hard Rule' },
  { ruleId: 'sat-004', applyAs: 'Hard Rule' },
  { ruleId: 'sat-005', applyAs: 'Hard Rule' },
  // Customised Rules - pre-applied with various settings
  { ruleId: 'cust-001', applyAs: 'Hard Rule' },
  { ruleId: 'cust-002', applyAs: 'Preference', priority: '2nd Priority' },
  { ruleId: 'cust-003', applyAs: 'Soft Rule' },
];

// Category options for filters and dropdowns
export const categoryOptions = [
  { value: 'Teacher Availability', label: 'Teacher Availability' },
  { value: 'Room Availability', label: 'Room Availability' },
  { value: 'No Consecutive', label: 'No Consecutive' },
  { value: 'Maximum Per Day', label: 'Maximum Per Day' },
  { value: 'Break Requirement', label: 'Break Requirement' },
  { value: 'Subject Timing', label: 'Subject Timing' },
  { value: 'Year Group', label: 'Year Group' },
  { value: 'General', label: 'General' },
  { value: 'Teacher', label: 'Teacher' },
  { value: 'Class', label: 'Class' },
  { value: 'Room', label: 'Room' },
  { value: 'School', label: 'School' },
];

export default {
  hardRules: mockHardRules,
  softRules: mockSoftRules,
  preferences: mockPreferences,
  teacherPreferences: mockTeacherPreferences,
  problemSolutions: mockProblemSolutions,
  automationProfiles: mockAutomationProfiles,
  unifiedRules: mockUnifiedRules,
  initialReviewList: mockInitialReviewList,
  categoryOptions,
};
